import { computed, ref } from 'vue'

import type { V2ConversationType } from '@/features/chat/types'
import type { V2UserDataType } from '@/features/users/types'

const temporaryConversationsRef = ref<Map<string, V2ConversationType>>(new Map())

export function useTemporaryConversations() {
  function createTemporaryConversation({
    sendToUserId,
    sendToUser,
    programId,
    currentUser,
  }: {
    sendToUserId: string
    sendToUser: V2UserDataType
    programId: string
    currentUser: V2UserDataType
  }): string {
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

    const temporaryConversation: V2ConversationType = {
      conversationId: tempId,
      programId,
      messages: [],
      unreadCount: 0,
      users: [
        {
          ...currentUser,
          hasSeen: true,
        },
        {
          ...sendToUser,
          hasSeen: false,
        },
      ],
    }

    temporaryConversationsRef.value.set(tempId, temporaryConversation)

    return tempId
  }

  function removeTemporaryConversation(conversationId: string) {
    temporaryConversationsRef.value.delete(conversationId)
  }

  function getTemporaryConversation(conversationId: string): V2ConversationType | undefined {
    return temporaryConversationsRef.value.get(conversationId)
  }

  function isTemporaryConversation(conversationId: string): boolean {
    return conversationId.startsWith('temp-') && temporaryConversationsRef.value.has(conversationId)
  }

  const allTemporaryConversationsRef = computed(() => {
    return Array.from(temporaryConversationsRef.value.values())
  })

  function convertTemporaryToReal(realConversationId: string, temporaryConversationId: string) {
    const tempConversation = temporaryConversationsRef.value.get(temporaryConversationId)
    if (tempConversation) {
      temporaryConversationsRef.value.delete(temporaryConversationId)
    }
  }

  return {
    createTemporaryConversation,
    removeTemporaryConversation,
    getTemporaryConversation,
    isTemporaryConversation,
    allTemporaryConversationsRef,
    convertTemporaryToReal,
  }
}
